import { useParams, Navigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Building2, MapPin } from "lucide-react";
import ContactForm from "@/components/ContactForm";
import Breadcrumbs from "@/components/Breadcrumbs";
import SEOHead from "@/components/SEOHead";
import {
  InlineCopy,
  LocationChoiceBlock,
  LocationFaqs,
  LocationFinalCta,
  LocationProofStrip,
  LocationSolutionsBlock,
  LocationSupportAndReviews,
} from "@/components/LocationSharedBlocks";
import { LOCATIONS, plainText } from "@/data/locationsContent";
import { fadeUp, stagger } from "@/utils/motionVariants";

/**
 * /locations/{city} — five North West city pages (handover v4, Sep 2026).
 *
 * Section order is fixed by the handover:
 *   hero + local intro → two customer-choice cards (hero action area) →
 *   proof strip → city situations → named local customers → six solutions →
 *   local support + review link → four local FAQs → nearby links + hub →
 *   retained general-enquiry form → final CTA.
 *
 * Copy lives in data/locationsContent.js. The retained enquiry form is the
 * standard ContactForm; submitted_from_url / page_url are stamped at submit
 * so CRM can see which city page the lead came from.
 */

export default function LocationPage() {
  const { location } = useParams();
  const data = LOCATIONS[location];
  if (!data) return <Navigate to="/locations" replace />;

  const path = `/locations/${location}`;

  return (
    <div data-testid={`${location}-page`}>
      <SEOHead
        rawTitle={data.seoTitle}
        description={data.metaDescription}
        path={path}
        faqs={data.faqs.map((f) => ({ q: f.q, a: plainText(f.a) }))}
        service={{
          name: `Card machines and payment support in ${data.title}`,
          description: data.metaDescription,
          serviceType: "Merchant Services",
          areaServed: data.areaServed,
        }}
      />

      {/* 1. Hero + local introduction, with the choice cards as its action area */}
      <section className="hero-gradient pt-16 pb-12 md:pt-24 md:pb-16" data-testid={`${location}-hero`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial="hidden" animate="visible" variants={stagger}>
            <Breadcrumbs className="mb-5" labels={{ [location]: data.title }} />
            <motion.p
              variants={fadeUp}
              className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.25em] text-brand-aqua-deep mb-4"
            >
              <MapPin size={12} aria-hidden="true" />
              {data.eyebrow}
            </motion.p>
            <motion.h1
              variants={fadeUp}
              className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-brand-black tracking-tight leading-tight mb-6"
            >
              {data.h1}
            </motion.h1>
            <motion.p variants={fadeUp} className="text-lg text-brand-grey-dark max-w-3xl leading-relaxed mb-10">
              {data.heroBody}
            </motion.p>
            {/* 2. Two customer-choice cards */}
            <motion.div variants={fadeUp} className="max-w-5xl">
              <LocationChoiceBlock embedded testId={`${location}-choice`} />
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* 3. Qualified proof strip */}
      <LocationProofStrip testId={`${location}-proof-strip`} />

      {/* 4. City-specific trading situations */}
      <section className="py-14 md:py-16 bg-white" data-testid={`${location}-situations`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-8">
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand-aqua-deep mb-3">{data.situations.eyebrow}</p>
            <h2 className="font-heading text-2xl sm:text-3xl font-bold text-brand-black tracking-tight">{data.situations.heading}</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {data.situations.items.map((item) => (
              <div key={item.heading} className="bg-gray-50 border border-brand-light-grey rounded-2xl p-6">
                <h3 className="font-heading text-lg font-bold text-brand-black mb-2">{item.heading}</h3>
                <p className="text-sm text-brand-grey-dark leading-relaxed">{item.body}</p>
              </div>
            ))}
          </div>
          {data.situations.note && <p className="text-xs text-brand-grey-dark mt-4">{data.situations.note}</p>}
        </div>
      </section>

      {/* 5. Named local customer examples */}
      <section className="py-14 md:py-16 bg-gray-50" data-testid={`${location}-customers`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-8">
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand-aqua-deep mb-3">{data.customers.eyebrow}</p>
            <h2 className="font-heading text-2xl sm:text-3xl font-bold text-brand-black tracking-tight">{data.customers.heading}</h2>
            {data.customers.note && <p className="text-sm text-brand-grey-dark mt-3">{data.customers.note}</p>}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {data.customers.items.map((c) => (
              <div key={c.name} className="bg-white border border-brand-light-grey rounded-2xl p-6 flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl bg-brand-aqua-light flex items-center justify-center flex-shrink-0">
                  <Building2 size={18} className="text-brand-aqua-deep" aria-hidden="true" />
                </div>
                <div>
                  <h3 className="font-heading text-lg font-bold text-brand-black mb-1.5">{c.name}</h3>
                  <p className="text-sm text-brand-grey-dark leading-relaxed">
                    <InlineCopy text={c.body} />
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 6. Six payment-solution cards */}
      <LocationSolutionsBlock locationName={data.name} testId={`${location}-solutions`} />

      {/* 7 + 8. Local support and customer-review link */}
      <LocationSupportAndReviews testId={`${location}-support`} />

      {/* 9. Four local FAQs */}
      <LocationFaqs heading={`${data.title} FAQs`} faqs={data.faqs} testIdPrefix={`${location}-faq`} />

      {/* 10. Nearby links and hub link */}
      <section className="py-10 bg-gray-50 border-y border-brand-light-grey/50" data-testid={`${location}-nearby`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-brand-aqua-light flex items-center justify-center flex-shrink-0">
                <MapPin size={18} className="text-brand-aqua-deep" aria-hidden="true" />
              </div>
              <h2 className="font-heading text-lg font-bold text-brand-black">Nearby areas</h2>
            </div>
            <nav aria-label="Nearby locations" className="flex flex-wrap gap-2">
              {data.nearby.map((n) => (
                <Link
                  key={n.to}
                  to={n.to}
                  className="inline-flex items-center gap-1.5 rounded-full border border-brand-light-grey bg-white hover:border-brand-aqua/50 hover:bg-brand-aqua-light/50 text-sm font-semibold text-brand-black px-4 py-2 transition-colors"
                >
                  {n.label}
                </Link>
              ))}
              <Link
                to="/locations"
                className="inline-flex items-center gap-1.5 rounded-full bg-brand-aqua-light text-brand-aqua-deep hover:bg-brand-aqua-deep hover:text-white text-sm font-semibold px-4 py-2 transition-colors"
                data-testid={`${location}-all-locations`}
              >
                All locations <ArrowRight size={14} />
              </Link>
            </nav>
          </div>
        </div>
      </section>

      {/* 11. Retained general-enquiry form */}
      <section className="py-14 md:py-16 bg-white" data-testid={`${location}-enquiry`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 items-start">
            <div className="lg:col-span-2">
              <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand-aqua-deep mb-3">General enquiry</p>
              {/* Single template string: adjacent text children would merge
                  into one DOM text node in the react-snap snapshot and break
                  hydration (React #418). */}
              <h2 className="font-heading text-2xl sm:text-3xl font-bold text-brand-black tracking-tight mb-4">
                {`Ask us about payments in ${data.title}`}
              </h2>
              <p className="text-sm text-brand-grey-dark leading-relaxed">
                Not ready for a statement review or estimate? Tell us where you trade and how customers pay, and we&apos;ll come back to you.
              </p>
            </div>
            <div className="lg:col-span-3 bg-white border border-brand-light-grey rounded-2xl p-6 sm:p-8 shadow-sm">
              <ContactForm context={{ locationLabel: data.name, location: location }} />
            </div>
          </div>
        </div>
      </section>

      {/* 12. Final two actions and online helper */}
      <LocationFinalCta testId={`${location}-final-cta`} />
    </div>
  );
}
